import { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { Modal, Spinner } from "./common";

export const DuplicadosPanel = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [dups, setDups] = useState(null); // null = a carregar
  const [dupKeep, setDupKeep] = useState({});
  const [busy, setBusy] = useState(false);

  const openDups = async () => {
    setOpen(true); setDups(null); setDupKeep({});
    try { const { data } = await api.get("/folders/duplicates"); setDups(data || []); }
    catch (e) { toast.error(formatApiError(e)); setOpen(false); }
  };

  const refreshQuiet = async () => {
    try { const { data } = await api.get("/folders/duplicates"); setDups(data || []); } catch { /* silêncio */ }
  };

  const repairSplit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/folders/repair-split-titles");
      toast.success((data.merged_groups || data.reattached)
        ? `Reparação concluída: ${data.reattached} ficha(s) repostas · ${data.merged_groups} grupo(s) fundidos · ${data.versions_moved} ficheiro(s) movidos`
        : "Nada a reparar — não há fichas divididas com nome exato igual");
      if (data.merged_groups || data.reattached) onChanged?.();
      refreshQuiet();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeGroup = async (g, gi) => {
    const keepId = dupKeep[gi] || g.items[0].id;
    setBusy(true);
    try {
      const remove_ids = g.items.filter((i) => i.id !== keepId).map((i) => i.id);
      const { data } = await api.post("/folders/merge", { keep_id: keepId, remove_ids });
      toast.success(`Fusão concluída: ${data.moved} ficheiro(s) movidos, ${data.conflicts} conflito(s) resolvidos, ${fmtBytes(data.freed_bytes)} libertados`);
      setDups((prev) => (prev || []).filter((_, i) => i !== gi));
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeAll = async () => {
    setBusy(true);
    let ok = 0, freed = 0, fails = 0;
    try {
      const groups = [...(dups || [])];
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const keepId = dupKeep[gi] || g.items[0].id;
        try {
          const { data } = await api.post("/folders/merge", { keep_id: keepId, remove_ids: g.items.filter((i) => i.id !== keepId).map((i) => i.id) });
          ok += 1; freed += data.freed_bytes || 0;
        } catch { fails += 1; }
      }
      toast.success(`Fusão em massa: ${ok} grupo(s) fundidos, ${fmtBytes(freed)} libertados${fails ? ` · ${fails} falharam` : ""}`);
      setDups([]); onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="ajustes-dups-btn" onClick={openDups}
        title="Deteta títulos duplicados ou quase iguais e funde com segurança, poupando quota"
        className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
        <Copy size={13} /> Procurar duplicados
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Títulos duplicados — fusão segura" testid="dups-modal">
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3 space-y-2" data-testid="dups-repair-card">
            <p className="text-[11px] text-slate-400"><b className="text-cyan-300">Reparação automática:</b> funde só fichas com o nome EXATO igual (ex.: série dividida em duas entradas) e repõe ficheiros presos a fichas arquivadas. Os quase-iguais abaixo continuam a precisar da tua confirmação.</p>
            <button data-testid="dups-repair-btn" onClick={repairSplit} disabled={busy}
              className="w-full px-3 py-2 rounded-lg text-[11px] font-semibold border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/15 transition-colors flex items-center justify-center gap-2">
              {busy ? <Spinner /> : "Reparar agora (só nomes exatos)"}
            </button>
          </div>
          {dups === null && <div className="flex justify-center py-8"><Spinner /></div>}
          {dups !== null && dups.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Sem duplicados — cada título existe uma só vez.</p>}
          {Array.isArray(dups) && dups.length > 1 && (
            <button data-testid="dup-merge-all" disabled={busy} onClick={mergeAll}
              className="btn-violet w-full px-3 py-2.5 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-2">
              {busy ? <Spinner /> : <><Copy size={12} /> Fundir TODOS ({dups.length} grupos — mantém o que tem mais ficheiros)</>}
            </button>
          )}
          {(dups || []).map((g, gi) => (
            <div key={gi} className="rounded-xl border border-[#232D42] p-3 space-y-2" data-testid={`dup-group-${gi}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-white truncate">{g.title} {g.year ? <span className="text-slate-500">({g.year})</span> : null}</p>
                <span className="text-[10px] font-mono2 text-amber-300 shrink-0">poupa {fmtBytes(g.savable_bytes)}</span>
              </div>
              {g.items.map((it) => (
                <label key={it.id} className="flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
                  <input type="radio" name={`dupkeep-${gi}`} data-testid={`dup-keep-${gi}-${it.id}`} checked={(dupKeep[gi] || g.items[0].id) === it.id} onChange={() => setDupKeep({ ...dupKeep, [gi]: it.id })} className="accent-amber-500" />
                  <span className="flex-1 truncate">{it.category || "(Sem pasta)"} · {it.versions} versão(ões) · {fmtBytes(it.size_bytes)}</span>
                </label>
              ))}
              <p className="text-[10px] text-slate-500">Mantém o título escolhido, move os ficheiros (links intactos), resolve conflitos ficando com a melhor qualidade e apaga da nuvem só as cópias não partilhadas.</p>
              <button data-testid={`dup-merge-${gi}`} onClick={() => mergeGroup(g, gi)} disabled={busy}
                className="btn-neon w-full px-3 py-2 rounded-lg text-[11px] flex items-center justify-center gap-2">
                {busy ? <Spinner /> : <><Copy size={12} /> Fundir no título selecionado</>}
              </button>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};
